import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

// Theme switcher state — user-level, persisted in localStorage. Drives a
// `data-theme="X"` attribute on <html>; tokens.css owns the CSS-variable
// overrides per theme so no component touches a theme name directly.
//
// In the future this may be enforced by the user's organization (server-
// side: the user's tenant carries a default theme). For now it's a personal
// preference, set from the TopBar ThemeSwitcher.

export type ThemeName = 'lop' | 'mizrahi' | 'discount';

const THEMES: readonly ThemeName[] = ['lop', 'mizrahi', 'discount'] as const;
const STORAGE_KEY = 'lop:theme';
// Default for anyone who never picked a theme (no stored choice) = Mizrahi (product owner, 23/10/2026; single Mizrahi theme since v0.1.97).
// A stored choice always wins, so users who explicitly picked another theme keep it.
const DEFAULT_THEME: ThemeName = 'mizrahi';

function isThemeName(v: unknown): v is ThemeName {
  return typeof v === 'string' && (THEMES as readonly string[]).includes(v);
}

function readInitialTheme(): ThemeName {
  if (typeof window === 'undefined') return DEFAULT_THEME;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    // Migration: the short-lived 'mizrahi2' theme IS the Mizrahi look now.
    if (stored === 'mizrahi2') {
      window.localStorage.setItem(STORAGE_KEY, 'mizrahi');
      return 'mizrahi';
    }
    if (isThemeName(stored)) return stored;
  } catch {
    /* localStorage can throw in privacy mode; fall through to default */
  }
  return DEFAULT_THEME;
}

// ── Display mode (light / dark / follow-the-OS) ────────────────────────────
// ORTHOGONAL to the colour theme: every theme has a light and a dark palette.
// `colorScheme` is the user's PREFERENCE ('system' follows prefers-color-scheme,
// live); `resolvedScheme` is what is actually painted. The resolved value is
// written to <html data-color-scheme="light|dark"> (+ inline `color-scheme`).
// public/index.html applies the same logic in a tiny inline script before React
// mounts so there is no flash of the wrong scheme — keep the two in sync.
export type ColorScheme = 'system' | 'light' | 'dark';
export type ResolvedScheme = 'light' | 'dark';

const COLOR_SCHEMES: readonly ColorScheme[] = ['system', 'light', 'dark'] as const;
const SCHEME_STORAGE_KEY = 'lop:colorScheme';
const DEFAULT_COLOR_SCHEME: ColorScheme = 'system';
const DARK_QUERY = '(prefers-color-scheme: dark)';

function isColorScheme(v: unknown): v is ColorScheme {
  return typeof v === 'string' && (COLOR_SCHEMES as readonly string[]).includes(v);
}

function readInitialColorScheme(): ColorScheme {
  if (typeof window === 'undefined') return DEFAULT_COLOR_SCHEME;
  try {
    const stored = window.localStorage.getItem(SCHEME_STORAGE_KEY);
    if (isColorScheme(stored)) return stored;
  } catch {
    /* privacy mode: fall through to default */
  }
  return DEFAULT_COLOR_SCHEME;
}

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(DARK_QUERY).matches
    : false;
}

interface ThemeContextValue {
  theme: ThemeName;
  setTheme: (next: ThemeName) => void;
  themes: readonly ThemeName[];
  colorScheme: ColorScheme;
  setColorScheme: (next: ColorScheme) => void;
  colorSchemes: readonly ColorScheme[];
  resolvedScheme: ResolvedScheme;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<ThemeName>(readInitialTheme);

  // Sync `data-theme` on <html> so the [data-theme="X"] selectors in
  // tokens.css activate. Done in an effect so SSR/initial render stays
  // pure; the brief flash before this runs is acceptable for a CSR app.
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  const [colorScheme, setColorSchemeState] = useState<ColorScheme>(readInitialColorScheme);
  const [systemDark, setSystemDark] = useState<boolean>(systemPrefersDark);

  // Live-follow the OS setting (only matters while colorScheme === 'system',
  // but tracking always keeps the state fresh when the user switches back).
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(DARK_QUERY);
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    setSystemDark(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  const resolvedScheme: ResolvedScheme =
    colorScheme === 'system' ? (systemDark ? 'dark' : 'light') : colorScheme;

  useEffect(() => {
    const el = document.documentElement;
    el.dataset.colorScheme = resolvedScheme;
    el.style.colorScheme = resolvedScheme;
  }, [resolvedScheme]);

  const setColorScheme = useCallback((next: ColorScheme) => {
    setColorSchemeState(next);
    try {
      window.localStorage.setItem(SCHEME_STORAGE_KEY, next);
    } catch {
      /* ignore — preference simply won't persist */
    }
  }, []);

  const setTheme = useCallback((next: ThemeName) => {
    setThemeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore — preference simply won't persist */
    }
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      setTheme,
      themes: THEMES,
      colorScheme,
      setColorScheme,
      colorSchemes: COLOR_SCHEMES,
      resolvedScheme,
    }),
    [theme, setTheme, colorScheme, setColorScheme, resolvedScheme],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used inside <ThemeProvider>');
  }
  return ctx;
}

// Banking-privacy note: on logout, we DO NOT clear the theme preference.
// A theme is a visual preference, not session data. The next user on a
// shared browser will see the previous user's chosen theme — that's
// fine; it's a paint setting, no tenant data leaks via it.
